import React, { useState } from 'react'
import axios from 'axios'
import {
  Card,
  CardContent,
  Typography,
  Chip,
  TextField,
  Select,
  MenuItem,
  IconButton,
  Grid
} from '@material-ui/core'
import { makeStyles } from '@material-ui/core/styles'
import { Edit as EditIcon } from '@material-ui/icons'

const useStyles = makeStyles(theme => ({
  root: {
    padding: theme.spacing(2)
  },
  card: {
    marginBottom: theme.spacing(2)
  },
  cardHeader: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
    marginBottom: theme.spacing(1)
  },
  nameRow: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1)
  },
  nameText: {
    cursor: 'pointer',
    '&:hover': {
      textDecoration: 'underline'
    }
  },
  metaRow: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(2),
    marginTop: theme.spacing(1)
  }
}))

const GROUP_TYPE_OPTIONS = [
  { value: 'base', label: 'BASE' },
  { value: 'alternate', label: 'ALT' },
  { value: 'allowance_group', label: 'ALLOW' },
  { value: 'unknown', label: 'UNKNOWN' }
]

const getChipProps = (groupType) => {
  switch (groupType) {
    case 'base':
      return { label: 'BASE', color: 'primary', style: {} }
    case 'alternate':
      return { label: 'ALT', color: 'default', style: { backgroundColor: '#4caf50', color: '#fff' } }
    case 'allowance_group':
      return { label: 'ALLOW', color: 'default', style: { backgroundColor: '#ff9800', color: '#fff' } }
    default:
      return { label: 'UNKNOWN', color: 'default', style: {} }
  }
}

const GroupCard = ({ group, onGroupUpdate, url }) => {
  const classes = useStyles()
  const [editing, setEditing] = useState(false)
  const [nameValue, setNameValue] = useState(group.name || '')
  const [saving, setSaving] = useState(false)

  const chipProps = getChipProps(group.groupType)

  const saveName = () => {
    const trimmed = nameValue.trim()
    if (!trimmed || trimmed === group.name) {
      setEditing(false)
      setNameValue(group.name || '')
      return
    }
    setSaving(true)
    axios.put(`${url}/api/bid-estimation/bid-groups/${group._id}`, { name: trimmed })
      .then(() => {
        setSaving(false)
        setEditing(false)
        onGroupUpdate()
      })
      .catch(err => {
        console.error('Failed to save group name:', err)
        setSaving(false)
        setEditing(false)
        setNameValue(group.name || '')
      })
  }

  const handleNameKeyDown = (e) => {
    if (e.key === 'Enter') {
      saveName()
    } else if (e.key === 'Escape') {
      setEditing(false)
      setNameValue(group.name || '')
    }
  }

  const handleTypeChange = (e) => {
    const newType = e.target.value
    axios.put(`${url}/api/bid-estimation/bid-groups/${group._id}`, { groupType: newType })
      .then(() => {
        onGroupUpdate()
      })
      .catch(err => {
        console.error('Failed to save group type:', err)
      })
  }

  return (
    <Card className={classes.card} variant="outlined">
      <CardContent>
        <div className={classes.cardHeader}>
          <Chip
            label={chipProps.label}
            color={chipProps.color}
            size="small"
            style={chipProps.style}
          />
          <Select
            value={group.groupType || 'unknown'}
            onChange={handleTypeChange}
            disableUnderline
            style={{ fontSize: 12, color: '#888' }}
          >
            {GROUP_TYPE_OPTIONS.map(opt => (
              <MenuItem key={opt.value} value={opt.value}>
                {opt.label}
              </MenuItem>
            ))}
          </Select>
        </div>

        <div className={classes.nameRow}>
          {editing ? (
            <TextField
              value={nameValue}
              onChange={e => setNameValue(e.target.value)}
              onBlur={saveName}
              onKeyDown={handleNameKeyDown}
              autoFocus
              disabled={saving}
              size="small"
              variant="outlined"
              style={{ minWidth: 240 }}
            />
          ) : (
            <>
              <Typography
                variant="subtitle1"
                className={classes.nameText}
                onClick={() => setEditing(true)}
              >
                {group.name || '(unnamed)'}
              </Typography>
              <IconButton
                size="small"
                onClick={() => setEditing(true)}
                aria-label="Edit group name"
              >
                <EditIcon fontSize="small" />
              </IconButton>
            </>
          )}
        </div>

        <div className={classes.metaRow}>
          {group.sourceSheet && (
            <Typography variant="caption" color="textSecondary">
              Sheet: {group.sourceSheet}
            </Typography>
          )}
          <Typography variant="caption" color="textSecondary">
            {group.itemCount != null ? group.itemCount : (group.items ? group.items.length : 0)} items
          </Typography>
        </div>
      </CardContent>
    </Card>
  )
}

const GroupsTab = ({ groups, onGroupUpdate, url }) => {
  const classes = useStyles()

  if (!groups || groups.length === 0) {
    return (
      <div className={classes.root}>
        <Typography color="textSecondary">No groups detected</Typography>
      </div>
    )
  }

  return (
    <div className={classes.root}>
      <Grid container spacing={2}>
        {groups.map(group => (
          <Grid item xs={12} sm={6} md={4} key={group._id}>
            <GroupCard
              group={group}
              onGroupUpdate={onGroupUpdate}
              url={url}
            />
          </Grid>
        ))}
      </Grid>
    </div>
  )
}

export default GroupsTab
